import {
  assetRecordCityLine,
  assetRecordDisplayTitle,
  assetRecordStreetCityLine,
} from '../components/AssetGetByIdView';
import { assetRecordPrimaryId } from './assetRecordPrimaryId';
import { assetAvgRating, assetReviewCount, pickAssetsInVicinity } from './homeAssetRanking';
import type { GeoCoordinates } from './assetGeo';

function normalizeKeyPart(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, ' ');
}

/**
 * One home-carousel card per physical venue — not per room/facility at the same address.
 */
export function homeCarouselVenueDedupeKey(asset: Record<string, unknown>): string {
  const title = normalizeKeyPart(assetRecordDisplayTitle(asset));
  const location = normalizeKeyPart(
    assetRecordStreetCityLine(asset, 'he') ?? assetRecordCityLine(asset, 'he') ?? '',
  );
  if (title && location) {
    return `${title}|${location}`;
  }
  if (title) {
    return `title:${title}`;
  }
  const id = assetRecordPrimaryId(asset);
  if (id) {
    return `id:${id}`;
  }
  return '';
}

function compareHomeCarouselAsset(
  candidate: Record<string, unknown>,
  incumbent: Record<string, unknown>,
): number {
  const ratingDiff = assetAvgRating(candidate) - assetAvgRating(incumbent);
  if (ratingDiff !== 0) {
    return ratingDiff;
  }
  return assetReviewCount(candidate) - assetReviewCount(incumbent);
}

/** Keep the best-rated asset per venue; preserve first-seen venue order. */
export function dedupeHomeCarouselByVenue(assets: Record<string, unknown>[]): Record<string, unknown>[] {
  const bestByKey = new Map<string, Record<string, unknown>>();
  const order: string[] = [];

  for (const asset of assets) {
    const key =
      homeCarouselVenueDedupeKey(asset) ||
      (assetRecordPrimaryId(asset) ? `id:${assetRecordPrimaryId(asset)}` : '');
    if (!key) {
      continue;
    }
    if (!bestByKey.has(key)) {
      order.push(key);
      bestByKey.set(key, asset);
      continue;
    }
    const existing = bestByKey.get(key)!;
    if (compareHomeCarouselAsset(asset, existing) > 0) {
      bestByKey.set(key, asset);
    }
  }

  return order.map((key) => bestByKey.get(key)!);
}

export function excludeHomeCarouselVenues(
  assets: Record<string, unknown>[],
  venueKeys: ReadonlySet<string>,
): Record<string, unknown>[] {
  if (venueKeys.size === 0) {
    return assets;
  }
  return assets.filter((asset) => {
    const key = homeCarouselVenueDedupeKey(asset);
    return !key || !venueKeys.has(key);
  });
}

export function homeCarouselVenueKeySet(assets: Record<string, unknown>[]): Set<string> {
  const keys = new Set<string>();
  for (const asset of assets) {
    const key = homeCarouselVenueDedupeKey(asset);
    if (key) {
      keys.add(key);
    }
  }
  return keys;
}

export function homeCarouselAssetIdSet(assets: Record<string, unknown>[]): Set<string> {
  const ids = new Set<string>();
  for (const asset of assets) {
    const id = assetRecordPrimaryId(asset);
    if (id) {
      ids.add(id);
    }
  }
  return ids;
}

/** Drop rows already shown in another carousel (by asset id — not whole venue). */
export function excludeHomeCarouselAssetsById(
  assets: Record<string, unknown>[],
  assetIds: ReadonlySet<string>,
): Record<string, unknown>[] {
  if (assetIds.size === 0) {
    return assets;
  }
  return assets.filter((asset) => {
    const id = assetRecordPrimaryId(asset);
    return !id || !assetIds.has(id);
  });
}

export function buildHomeVicinityCarousel(
  recommended: Record<string, unknown>[],
  vicinityRaw: Record<string, unknown>[],
  catalog: Record<string, unknown>[],
  coordinates: GeoCoordinates | null | undefined,
  limit: number,
): Record<string, unknown>[] {
  const recommendedIds = homeCarouselAssetIdSet(recommended);
  const vicinity = dedupeHomeCarouselByVenue(
    excludeHomeCarouselAssetsById(vicinityRaw, recommendedIds),
  ).slice(0, limit);

  if (vicinity.length >= limit) {
    return vicinity;
  }

  const usedIds = new Set<string>([
    ...Array.from(recommendedIds),
    ...Array.from(homeCarouselAssetIdSet(vicinity)),
  ]);
  const fill = dedupeHomeCarouselByVenue(
    excludeHomeCarouselAssetsById(
      pickAssetsInVicinity(catalog, coordinates, Math.max(limit * 3, limit)),
      usedIds,
    ),
  ).slice(0, limit - vicinity.length);

  return [...vicinity, ...fill];
}
